import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"], summary';

export function getFocusable(root: ParentNode | null | undefined): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('aria-hidden') && el.getAttribute('aria-disabled') !== 'true',
  );
}

/** Focuses the first focusable descendant; returns whether something received focus. */
export function focusFirst(root: ParentNode | null | undefined): boolean {
  const [first] = getFocusable(root);
  if (!first) return false;
  first.focus();
  return document.activeElement === first;
}

/**
 * Moves focus to a heading (or any element) programmatically. Used for calm-mode screen transitions (§13.2:
 * "Instant replacement with focus on the heading") and after route changes.
 */
export function focusHeading(el: HTMLElement | null | undefined): void {
  if (!el) return;
  if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
  el.focus({ preventScroll: false });
}

/** Focuses the ref'd heading on mount (and whenever `key` changes). */
export function useFocusHeading<T extends HTMLElement>(key?: unknown): RefObject<T | null> {
  const ref = useRef<T>(null);
  useEffect(() => {
    focusHeading(ref.current);
  }, [key]);
  return ref;
}

/** Remembers the element focused when the hook mounted and restores focus on unmount (modals, takeovers). */
export function useReturnFocus(active = true): void {
  const previous = useRef<Element | null>(null);
  useEffect(() => {
    if (!active) return;
    previous.current = document.activeElement;
    return () => {
      const el = previous.current;
      if (el instanceof HTMLElement && el.isConnected) el.focus();
    };
  }, [active]);
}

/** Roving tabindex helper for grids/lists: returns the next index for an arrow key. */
export function nextIndexForKey(key: string, index: number, count: number, columns = 1): number | null {
  if (count === 0) return null;
  switch (key) {
    case 'ArrowRight':
      return (index + 1) % count;
    case 'ArrowLeft':
      return (index - 1 + count) % count;
    case 'ArrowDown':
      return Math.min(index + columns, count - 1);
    case 'ArrowUp':
      return Math.max(index - columns, 0);
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}
